import { useQueries } from "@tanstack/react-query";
import Axios from "../../../Axios/Axios";
import { Urls } from "../../../Urls";

/*
  Spot Overview tab needs three endpoints in parallel:
    - spot_metrics   -> headline KPIs (spend, net saving, enquiries, avg quotes)
    - spot_funnel    -> funnel by enquiry / by truckload
    - top_transporters -> business share + savings by transporter

  NOTE: the "By Plant" business share / savings cards in the mockup have no
  matching endpoint on the backend (confirmed against routes/spot_overview.py
  — only spot_metrics, spot_funnel, and top_transporters exist there).
  Dropped from SpotOverviewTab rather than stubbed.

  IMPORTANT: `plant` and `destination` here must be the raw location_name
  string (e.g. "Mundka"), NOT a location_code — the backend matches
  lane_details.origin.location.location / lane_details.destination.location_name
  directly. Get these values from useSpotDashboardFilters, not the generic
  Indent Summary filters hook.

  All three share the same filter shape as the rest of the dashboard, so we
  build one params object and reuse it.
*/

const buildParams = ({ startDate, endDate, plant, transporterId, role, destination }) => {
  const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
  if (plant && plant !== "All") params.append("plant", plant);
  if (transporterId && transporterId !== "All") params.append("transporter_id", transporterId);
  if (role) params.append("role", role);
  if (destination && destination !== "All") params.append("destination", destination);
  return params;
};

export const useSpotOverview = (
  { startDate, endDate, plant, transporterId, role, destination },
  enabled = true
) => {
  const filters = { startDate, endDate, plant, transporterId, role, destination };
  const queryKeyBase = [startDate, endDate, plant, transporterId, role, destination];

  const results = useQueries({
    queries: [
      {
        queryKey: ["spotOverviewMetrics", ...queryKeyBase],
        queryFn: async () => {
          const params = buildParams(filters);
          const { data } = await Axios.get(`${Urls.tmsDashboardSpotMetrics}?${params.toString()}`);
          return data;
        },
        enabled,
        staleTime: 60 * 1000,
        refetchInterval: enabled ? 5 * 60 * 1000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: ["spotOverviewFunnel", ...queryKeyBase],
        queryFn: async () => {
          const params = buildParams(filters);
          const { data } = await Axios.get(`${Urls.tmsDashboardSpotFunnel}?${params.toString()}`);
          return data;
        },
        enabled,
        staleTime: 60 * 1000,
        refetchInterval: enabled ? 5 * 60 * 1000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: ["spotOverviewTopTransporters", ...queryKeyBase],
        queryFn: async () => {
          const params = buildParams(filters);
          params.append("skip", "0");
          params.append("limit", "50");
          const { data } = await Axios.get(`${Urls.tmsDashboardSpotTopTransporters}?${params.toString()}`);
          return data;
        },
        enabled,
        staleTime: 60 * 1000,
        refetchInterval: enabled ? 5 * 60 * 1000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
      },
      {
        // Not a "top lanes" widget here — fetched purely to roll up into
        // "Business Share / Savings: By Plant" client-side (grouped by
        // `origin`). Needs every lane, not a page of them, so limit is set
        // high rather than using the endpoint's own default of 20.
        queryKey: ["spotOverviewLanesForPlantRollup", ...queryKeyBase],
        queryFn: async () => {
          const params = buildParams(filters);
          params.append("lane_type", "all");
          params.append("skip", "0");
          params.append("limit", "1000");
          const { data } = await Axios.get(`${Urls.tmsDashboardSpotLanes}?${params.toString()}`);
          return data;
        },
        enabled,
        staleTime: 60 * 1000,
        refetchInterval: enabled ? 5 * 60 * 1000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
      },
    ],
  });

  const [metricsQuery, funnelQuery, transportersQuery, lanesQuery] = results;

  return {
    metrics: metricsQuery.data,
    funnel: funnelQuery.data,
    topTransporters: transportersQuery.data,
    lanes: lanesQuery.data,
    isLoading:
      metricsQuery.isLoading || funnelQuery.isLoading || transportersQuery.isLoading || lanesQuery.isLoading,
    isError: metricsQuery.isError || funnelQuery.isError || transportersQuery.isError || lanesQuery.isError,
    dataUpdatedAt: Math.max(
      metricsQuery.dataUpdatedAt || 0,
      funnelQuery.dataUpdatedAt || 0,
      transportersQuery.dataUpdatedAt || 0,
      lanesQuery.dataUpdatedAt || 0
    ),
  };
};